import { D3DragEvent, DragBehavior, EnterElement, ScaleLinear, Selection, axisBottom, drag } from "d3"
import { MarginedBoundingBox } from "../../Types/MarginedBoundingBox"
import { D3OneToOneRenderable } from "./D3OneToOneRenderable"
import { ReactCallbacks } from "../../Types/ReactCallbacks"
import { MobergFontFamily, MobergFontSize } from "../../../../../../Moberg"
import { AXIS_TICK_FONT_SIZE } from "../Visualizations/Constants"
import { D3DragOverlay } from "./D3DragOverlay"

export type D3LinearXAxisConfig = {
    boundingBox: MarginedBoundingBox
    scale: ScaleLinear<any, any>
    dragDisabled?: boolean
    label?: string
    onDrag?: () => void
    onDoubleClick?: () => void
    onDragEnd?: () => void
    onDragStart?: () => void
}

export class D3LinearXAxis extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3LinearXAxisConfig> {
    private axisClassName: string = "d3-histogram-x-axis"
    private labelClassName: string = "d3-histogram-x-axis-label"

    private dragOverlay?: D3DragOverlay
    private height: number = 30
    private dragBehavior: DragBehavior<any, any, any>

    constructor(root: SVGGElement, config: D3LinearXAxisConfig, reactCallbacks: ReactCallbacks<any>) {
        super(root, config, "d3-histogram-x-axis-container", reactCallbacks)
       
        this.dragBehavior = drag()
            .on("start", this.onDragStart)
            .on("drag", this.onDrag)
            .on("end", this.onDragEnd)

        this.render()
    }

    protected enter(newElements: Selection<EnterElement, D3LinearXAxisConfig, any, any>): Selection<SVGGElement, D3LinearXAxisConfig, SVGGElement, any> {
        // Create the Axis Group
        const axisGroup = newElements
            .append("g")
            .attr("class", this.className)
            .attr("transform", `translate(0, ${this.config.boundingBox.height})`)
            .style("color", "var(--color-chart-axis-text)")

        // Calculate tick values if necessary
        const axis = axisBottom(this.config.scale)

        axisGroup.append("g")
            .attr("class", this.axisClassName)
            .call(axis)
            .style("user-select", "none") // disables highlighting the ticks
            .style("font-size", AXIS_TICK_FONT_SIZE)
            .style("font-family", MobergFontFamily.REGULAR)
            .style("text-anchor", "middle")

        // create the label
        axisGroup.append("text")
            .attr("text-anchor", "middle")
            .attr("class", this.labelClassName)
            .attr("transform", `translate(${this.config.scale.range()[1] / 2}, 40)`)
            .text(() => this.config.label ?? "")
            .attr("fill", "var(--color-chart-axis-text)")

        axisGroup.each((config, i, nodes) => this.createChildren(config, i, nodes))

        return axisGroup
    }

    protected update(updatedElements: Selection<SVGGElement, D3LinearXAxisConfig, any, any>): Selection<SVGGElement, D3LinearXAxisConfig, SVGGElement, any> {
        const axisGroup = updatedElements.attr("transform", `translate(0, ${this.config.boundingBox.height})`)

        // Redraw the axis
        const axis = axisBottom(this.config.scale)
        const d3Axis = axisGroup.select("." + this.axisClassName) as Selection<SVGGElement, any, any, any>
        d3Axis.call(axis)

        // Update the label
        axisGroup.select("." + this.labelClassName)
            .attr("transform", `translate(${this.config.scale.range()[1] / 2}, 40)`)
            .text(() => this.config.label ?? "")
            .style("font-size", MobergFontSize.LARGE)
            .style("font-family", MobergFontFamily.REGULAR)

        this.renderChildren()

        return updatedElements
    }

    protected updateDerivedState(): void {
        this.updateChildren()
    }

    protected createChildren(config: D3LinearXAxisConfig, index: number, nodes: ArrayLike<SVGGElement>): void {
        const root = nodes[index]
        const width = config.scale.range()[1]
        const boundingBox = { x: 0, y: 0, width, height: this.height }

        this.dragOverlay = new D3DragOverlay(root, { boundingBox, dragBehavior: this.dragBehavior, disabled: config.dragDisabled }, this.reactCallbacks, { onDoubleClick: this.config?.onDoubleClick })
    }

    protected updateChildren = () => {
        const width = this.config.scale.range()[1]
        const boundingBox = { x: 0, y: 0, width, height: this.height }
        this.dragOverlay?.updateConfig({ boundingBox, dragBehavior: this.dragBehavior, disabled: this.config.dragDisabled })
    }

    protected renderChildren = () => {
        this.dragOverlay?.render()
    }

    private onDrag = (dragEvent: D3DragEvent<any, any, any>) => {
        const min = this.config.scale.domain()[0]
        const max = this.config.scale.domain()[1]
        const width = this.config.scale.range()[1]

        const range = max - min
        const sensitivity = range / width
        let { dx, dy } = dragEvent

        const pointerX = dragEvent.x + dx
        const zoomScaling = pointerX / width

        const newMinX = min - dx * sensitivity - (dy * sensitivity) * zoomScaling
        const newMaxX = max - dx * sensitivity + (dy * sensitivity) * (1 - zoomScaling)

        this.config.scale.domain([newMinX, newMaxX])

        if (this.config.onDrag) {
            this.config.onDrag()
        }

        requestAnimationFrame(() => {
            this.render()
        })
    }

    private onDragEnd = () => {
        if (this.config.onDragEnd) {
            this.config.onDragEnd()
        }
    }

    private onDragStart = () => {
        if (this.config.onDragStart) {
            this.config.onDragStart()
        }
    }
}
